import React, { useState } from "react";
import {
  Grid,
  Button,
  Typography,
  Paper,
  Box,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import { styled } from "@mui/material/styles";
import PublishIcon from "@material-ui/icons/Publish";
import GetAppIcon from "@material-ui/icons/GetApp";

// components
import PageTitle from "../../components/PageTitle";
import Widget from "../../components/Widget";

// service
import {
  downloadTransactionCorrectionTemplate,
  uploadTransactionCorrectionExcel,
} from "../../service/TransactionRequest";
import { ERROR_CODE_SUCCESS } from "../../components/common/constant/ResponseErrorCodeConstant";

const useStyles = makeStyles((theme) => ({
  uploadSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  uploadButton: {
    position: "relative",
    overflow: "hidden",
  },
  fileInput: {
    position: "absolute",
    opacity: 0,
    width: "100%",
    height: "100%",
    cursor: "pointer",
  },
  fileName: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  resultSection: {
    marginTop: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function FixNewOrder() {
  const classes = useStyles();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [processStats, setProcessStats] = useState(null);

  // 下载 Excel 模板
  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadTransactionCorrectionTemplate();
      
      // 创建 blob 链接并下载
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "transaction-correction-template.xlsx");
      // 添加到页面
      document.body.appendChild(link);
      // 触发下载
      link.click();
      // 清理并移除链接
      link.parentNode.removeChild(link);
      // 释放 URL 对象
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("下载模板失败:", error);
      alert("下载模板失败，请稍后重试");
    }
  };

  // 处理文件选择
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setFileName(file.name);
      setUploadResult(null);
    }
  };

  // 上传文件
  const handleUpload = async () => {
    if (!selectedFile) {
      alert("请先选择要上传的文件");
      return;
    }

    setIsUploading(true);
    setUploadResult(null);
    setProcessStats(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await uploadTransactionCorrectionExcel(formData);

      if (response && response.code === ERROR_CODE_SUCCESS) {
        // 成功响应
        const stats = response.data || {};
        setProcessStats({
          processedCount: stats.processedCount || 0,
          successCount: stats.successCount || 0,
          failedCount: stats.failedCount || 0,
          failureReason: stats.failureReason || null,
        });

        setUploadResult({
          success: true,
          message: `处理完成！共处理 ${stats.processedCount || 0} 条，成功 ${stats.successCount || 0} 条，失败 ${stats.failedCount || 0} 条`,
        });
      } else {
        // 失败响应
        const stats = response.data || {};
        setProcessStats({
          processedCount: stats.processedCount || 0,
          successCount: stats.successCount || 0,
          failedCount: stats.failedCount || 0,
          failureReason: stats.failureReason || response.message || null,
        });

        setUploadResult({
          success: false,
          message: response.message || "文件处理失败，请稍后重试",
        });
      }
    } catch (error) {
      console.error("上传失败:", error);
      setUploadResult({
        success: false,
        message: error.message || error.response?.data?.message || "文件上传失败，请稍后重试",
      });
    } finally {
      setIsUploading(false);
    }
  };


  return (
    <>
      <PageTitle title="Fix New Order" />
      <Grid container spacing={4}>
        {/* 操作区域 */}
        <Grid item xs={12}>
          <Widget title="Upload Excel File" disableWidgetMenu>
            <Paper className={classes.paper}>
              <Typography variant="h6" gutterBottom>
                操作步骤：
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                1. 点击"下载模板"按钮下载 Excel 模板文件
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                2. 按照模板格式填写订单信息
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                3. 选择填写好的 Excel 文件并上传
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                4. 查看处理结果
              </Typography>

              <Box mt={3}>
                <Typography variant="h6" gutterBottom color="error">
                  注意事项：
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  • <strong>Transaction ID</strong> 和 <strong>Acquirer Transaction ID</strong> 填写时，请在前面加上单引号 <code>'</code>（例如：<code>'TXN123456</code>），以避免 Excel 自动格式化
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  • <strong>Transaction Amount</strong> 单位为元，例如：12.34 SGD 的交易应填写 <code>12.34</code>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • 支持的 <strong>Transaction Method</strong> 包括：<code>VISA</code>、<code>MASTERCARD</code>、<code>UNIONPAY</code>、<code>DINERS</code>
                </Typography>
              </Box>

              <Box className={classes.buttonGroup}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  onClick={handleDownloadTemplate}
                >
                  下载模板
                </Button>

                <Button
                  component="label"
                  variant="contained"
                  color="secondary"
                  startIcon={<PublishIcon />}
                  className={classes.uploadButton}
                  disabled={isUploading}
                >
                  选择文件
                  <VisuallyHiddenInput
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                  />
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                >
                  {isUploading ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: 8 }} />
                      上传中...
                    </>
                  ) : (
                    "上传文件"
                  )}
                </Button>
              </Box>

              {fileName && (
                <Typography className={classes.fileName}>
                  已选择文件: {fileName}
                </Typography>
              )}

              {uploadResult && (
                <Box mt={2}>
                  <Paper
                    style={{
                      padding: 16,
                      backgroundColor: uploadResult.success
                        ? "#e8f5e9"
                        : "#ffebee",
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        color: uploadResult.success ? "#2e7d32" : "#c62828",
                        fontWeight: "bold",
                        marginBottom: processStats ? 12 : 0,
                      }}
                    >
                      {uploadResult.message}
                    </Typography>
                    {processStats && (
                      <Box mt={1}>
                        <Typography variant="body2" style={{ marginBottom: 4 }}>
                          <strong>处理统计：</strong>
                        </Typography>
                        <Typography variant="body2" style={{ marginLeft: 16 }}>
                          总处理数：{processStats.processedCount} 条
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{
                            marginLeft: 16,
                            color: "#2e7d32",
                          }}
                        >
                          成功数：{processStats.successCount} 条
                        </Typography>
                        {processStats.failedCount > 0 && (
                          <Typography
                            variant="body2"
                            style={{
                              marginLeft: 16,
                              color: "#c62828",
                            }}
                          >
                            失败数：{processStats.failedCount} 条
                          </Typography>
                        )}
                        {processStats.failureReason && (
                          <Box mt={2}>
                            <Typography
                              variant="body2"
                              style={{
                                color: "#c62828",
                                fontWeight: "bold",
                              }}
                            >
                              失败原因：
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{
                                color: "#c62828",
                                marginLeft: 16,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {processStats.failureReason}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}
            </Paper>
          </Widget>
        </Grid>

        {/* 空状态提示 */}
        {!isLoading && !uploadResult && (
          <Grid item xs={12}>
            <Widget title="处理结果" disableWidgetMenu>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <Typography variant="body1" color="textSecondary">
                  暂无处理结果，请先上传文件
                </Typography>
              </Box>
            </Widget>
          </Grid>
        )}
      </Grid>
    </>
  );
}
